import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { AssetType } from '@modules/cards/types/asset.types';
import { MediaAdapter } from '@modules/content-api/adapter/media.adapter';
import { ContentAPIService } from '@modules/content-api/content-api.service';
import {
  ContactCardImageStyle,
  DefaultCardImageStyle,
} from '@modules/content-api/domain/image-styles/card-image.style';
import { MediaImageInterface, MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaVideoInterface, MediaVideoModel } from '@modules/media/models/media-video.model';
import { LocalizedDatePipe } from '@modules/pipes/localized-date.pipe';
import { SearchResult } from '@modules/section/dynamic-section/search-result.model';
import { environment } from 'src/environments/environment';
import { FakeImageAPI, FakeVideoAPI } from 'src/helpers/fake-api';

import { SortOptionInterface } from '../interfaces/sort-option.interface';
import { TagSchema, TagMenu } from '../schemas/tag.schema';

export type EntityType =
  | 'media-document'
  | 'media-image'
  | 'mb_article'
  | 'mb_presskit'
  | 'press_release'
  | 'media-video_file'
  | 'media-audio_file'
  | 'mb_contact'
  | 'event'
  | 'mb_page'
  | 'mb_media-collection'
  | 'media-technical_data';

export type PressReleaseType = 'mb_article' | 'mb_presskit';

export type SearchOptions = {
  entity: AssetType;
  page?: number;
  search?: string;
  topic?: number;
  tags?: number[];
  pressReleaseType?: PressReleaseType;
  sortOption?: SortOptionInterface;
};

// TODO: implement as zod schema
export type SearchResponse = {
  meta: {
    total: number;
    page: number;
    // perPage: number;
    // tagsCounts: { [k: number]: number };
    tags: number[];
    availableTags: number[];
    countsPerType: { [k in AssetType]?: number };
  };
  items: SearchResult[];
};

// Entities for which we show creation_date instead of display_date: raw media files
// (often missing an editor-set publication date). Press content (articles, press kits)
// keeps display_date, and mb_page (media-special) pages get an aggregated display_date
// from the API (their event's start date, falling back to creation_date).
const CREATION_DATE_ENTITY_TYPES: EntityType[] = [
  'media-document',
  'media-image',
  'media-video_file',
  'media-audio_file',
  'media-technical_data',
];

const ENTITY_FOR_TYPE: { [k in AssetType]: EntityType } = {
  DOCUMENT: 'media-document',
  IMAGE: 'media-image',
  PRESS_INFORMATION: 'mb_article',
  PRESS_KIT: 'mb_presskit',
  PRESS_RELEASE: 'press_release',
  VIDEO: 'media-video_file',
  AUDIO: 'media-audio_file',
  EVENT: 'event',
  MEDIA_COLLECTION: 'mb_media-collection',
  MEDIA_SPECIAL: 'mb_page',
  PRESS_CONTACT: 'mb_contact',
  TECHNICAL_DATA: 'media-technical_data',
};

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private apiService = inject(ContentAPIService);
  private localizedDate = inject(LocalizedDatePipe);
  private mediaAdapter = inject(MediaAdapter);
  private document = inject(DOCUMENT);

  // Emits all available tags
  public tags$: Observable<TagMenu[]>;

  constructor() {
    this.tags$ = this.getTags$();
  }

  public search(searchOptions: SearchOptions): Observable<SearchResponse> {
    const params = {
      entity: ENTITY_FOR_TYPE[searchOptions.entity],
      ...(searchOptions.page && { page: searchOptions.page }),
      ...(searchOptions.search && { search: searchOptions.search }),
      ...(searchOptions.topic && { topic: searchOptions.topic }),
      ...(searchOptions.tags && { tags: searchOptions.tags.join(',') }),
      ...(searchOptions.pressReleaseType && { press_release_type: searchOptions.pressReleaseType }),
      ...(searchOptions.sortOption?.value && {
        sort: searchOptions.sortOption.value,
      }),
      ...(searchOptions.sortOption?.order && { order: searchOptions.sortOption.order }),
    };

    return this.apiService.getUnparsedResources(`search/full`, params).pipe(
      map((data: any) => {
        // TODO: add zod schema to parse and type the API data

        // get the number of available content per type
        const countsPerType: { [k in AssetType]?: number } = {};
        Object.entries(ENTITY_FOR_TYPE).map(([key, value]) => {
          countsPerType[key as AssetType] = data.facets.entity_types[value];
        });
        const meta = {
          total: data.meta.total as number,
          page: data.meta.page as number,
          availableTags: data.facets.tags_available as number[],
          tags: Object.keys(data.facets.tags).map((tag) => Number(tag)) as number[],
          countsPerType,
        };

        const items = (data.results || [])
          .map((item: any) => {
            try {
              const header_image = item.header_image_uuid || item.image_uuid || item.poster_image_uuid;
              item.header_image_uuid = header_image;
              item.header_image = header_image ? `${environment.mediaDeliveryUrl}/${header_image}/4_3_400` : '';

              if (CREATION_DATE_ENTITY_TYPES.includes(ENTITY_FOR_TYPE[searchOptions.entity]) && item.creation_date) {
                item.display_date = item.creation_date;
              }

              if (item.display_date) {
                item.raw_display_date = item.display_date;
                item.display_date = this.localizedDate.transform(item.display_date, 'longDate');
              }

              // Parse into media objects
              switch (ENTITY_FOR_TYPE[searchOptions.entity]) {
                case 'mb_article':
                case 'mb_presskit':
                case 'press_release':
                  if (item.header_image_uuid) {
                    item.media = new MediaImageModel(
                      this.mediaAdapter.parse(
                        FakeImageAPI({
                          id: item.header_image_uuid,
                          name: item.header_image_name,
                          width: 400,
                          height: 300,
                        }),
                        DefaultCardImageStyle
                      ) as MediaImageInterface
                    );
                  }
                  break;

                case 'media-image':
                  if (item.image_uuid) {
                    item.media = new MediaImageModel(
                      this.mediaAdapter.parse(
                        FakeImageAPI({
                          id: item.image_uuid,
                          name: item.name,
                          width: 400,
                          height: 300,
                          mars_origin_id: item.mars_origin_id,
                          mars_shelf_number: item.mars_shelf_number,
                        }),
                        DefaultCardImageStyle
                      ) as MediaImageInterface
                    );
                  }
                  break;

                case 'media-video_file': {
                  item.media = item.video_url
                    ? new MediaVideoModel(
                        this.mediaAdapter.parse(
                          FakeVideoAPI({
                            id: item.id,
                            url: item.video_url,
                            name: item.name,
                            playingTime: item.playing_time,
                            mimetype: item.mimetype ?? '',
                            filesize: item.filesize,
                            posterImageId: item.poster_image_uuid,
                            mars_origin_id: item.mars_origin_id,
                            mars_shelf_number: item.mars_shelf_number,
                          })
                        ) as MediaVideoInterface
                      )
                    : null;
                  break;
                }

                case 'mb_contact':
                  if (item.contact_image_uuid) {
                    item.media = new MediaImageModel(
                      this.mediaAdapter.parse(
                        FakeImageAPI({ id: item.contact_image_uuid, name: item.name, width: 200, height: 200 }),
                        ContactCardImageStyle
                      ) as MediaImageInterface
                    );
                  }
                  break;
                case 'event':
                  if (item.event_image_uuid) {
                    item.media = new MediaImageModel(
                      this.mediaAdapter.parse(
                        FakeImageAPI({ id: item.event_image_uuid, name: item.title, width: 400, height: 300 }),
                        DefaultCardImageStyle
                      ) as MediaImageInterface
                    );
                  }
                  break;
                case 'mb_page':
                  if (item.header_image_uuid) {
                    item.media = new MediaImageModel(
                      this.mediaAdapter.parse(
                        FakeImageAPI({ id: item.header_image_uuid, name: item.title, width: 400, height: 300 }),
                        DefaultCardImageStyle
                      ) as MediaImageInterface
                    );
                  }
                  break;
                case 'media-document':
                case 'media-technical_data':
                default: {
                  item.media = undefined;
                  break;
                }
              }

              return item;
            } catch (error) {
              console.error('Error parsing a search result.', error);
              return false;
            }
          })
          .filter(Boolean)
          .map(
            (item: any) => new SearchResult({ ...item, type: searchOptions.entity }, this.document.location)
          ) as SearchResult[];
        return {
          meta,
          items,
        };
      })
    );
  }

  /**
   * Fetches all available tags from the backend.
   * @returns Observable of all available tags
   */
  private getTags$() {
    return this.apiService.getStaticFile(`menu/tags.json`, {}).pipe(
      map((data: any) => {
        try {
          const results = TagSchema.array().safeParse(data);
          if (results.success) {
            return results.data;
          } else {
            console.error(results.error);
            return [];
          }
        } catch {
          return [];
        }
      }),
      shareReplay(1)
    );
  }
}
